import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { nextSliceStart } from './circular';

/**
 * How long the skeleton shimmer flashes on a page change before the new window
 * of suggestions is revealed. A short beat that makes it obvious the list swapped
 * (a silent in-place content change reads as "nothing happened").
 */
export const PAGE_FLASH_MS = 400;

/** The navigation surface consumed by the arrow controls beneath the list. */
export interface SuggestionNav {
  /** 0-based index of the current page within the revealed history. */
  index: number;
  /** How many pages have been revealed so far (1 = only the first). */
  count: number;
  /** There's a previously-seen page behind the cursor. */
  canBack: boolean;
  /** There's an already-revealed page ahead of the cursor. */
  canForward: boolean;
  back: () => void;
  forward: () => void;
}

export interface SuggestionPagerState extends SuggestionNav {
  /** Window start into the pool — feed to `circularSlice`. */
  start: number;
  /** True during the brief skeleton flash right after a page change. */
  flashing: boolean;
  /** "Refresh suggestions": advance one page, revealing a new one at the frontier. */
  next: () => void;
  /** Reset back to the first page (call when a fresh pool/batch is fetched). */
  reset: () => void;
}

/**
 * Local pager for a windowed pool of suggestions (e.g. the 45 godaddy domains,
 * shown 3 at a time). Keeps a browser-style history of the window starts the user
 * has actually revealed by pressing "Refresh", plus a cursor into it:
 *
 * - `next()` (the Refresh button) advances the cursor, revealing a brand-new page
 *   only once the cursor sits at the frontier — so it pages forward through the
 *   pool exactly as before.
 * - `back()` / `forward()` move ONLY within that revealed history (never across
 *   the whole pool), so the arrows expose just the pages the user has already
 *   seen, not all 15 at once.
 *
 * Every move triggers a `PAGE_FLASH_MS` skeleton flash (`flashing`) so the swap is
 * visible. All callbacks are identity-stable (functional updates + refs), so the
 * consuming context memo isn't thrashed as the pool loads.
 */
export function useSuggestionPager(poolLength: number, pageSize: number): SuggestionPagerState {
  const [state, setState] = useState<{ history: number[]; cursor: number }>({ history: [0], cursor: 0 });
  const [flashing, setFlashing] = useState(false);
  const timer = useRef<number | null>(null);

  // Latest state + pool sizing for the stable callbacks (reading refs keeps their
  // identity fixed, so they don't re-create every time the pool changes size).
  const stateRef = useRef(state);
  stateRef.current = state;
  const poolRef = useRef(poolLength);
  poolRef.current = poolLength;
  const sizeRef = useRef(pageSize);
  sizeRef.current = pageSize;

  const flash = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setFlashing(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setFlashing(false);
    }, PAGE_FLASH_MS);
  }, []);

  // Clear a pending flash timer on unmount.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const reset = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setFlashing(false);
    setState({ history: [0], cursor: 0 });
  }, []);

  const back = useCallback(() => {
    if (stateRef.current.cursor <= 0) return;
    flash();
    setState((p) => ({ ...p, cursor: p.cursor - 1 }));
  }, [flash]);

  const forward = useCallback(() => {
    const { cursor, history } = stateRef.current;
    if (cursor >= history.length - 1) return;
    flash();
    setState((p) => ({ ...p, cursor: p.cursor + 1 }));
  }, [flash]);

  const next = useCallback(() => {
    const { cursor, history } = stateRef.current;
    // Advance within already-revealed pages first (same effect as the forward arrow).
    if (cursor < history.length - 1) {
      flash();
      setState((p) => ({ ...p, cursor: p.cursor + 1 }));
      return;
    }
    // At the frontier: reveal the next page unless the whole pool is already shown.
    const len = poolRef.current;
    const size = sizeRef.current;
    const totalPages = Math.max(1, Math.ceil(len / size));
    if (history.length >= totalPages) {
      // Fully revealed — cycle within the revealed pages (no-op for a single page).
      if (history.length > 1) {
        flash();
        setState((p) => ({ ...p, cursor: (p.cursor + 1) % p.history.length }));
      }
      return;
    }
    flash();
    setState((p) => ({
      history: [...p.history, nextSliceStart(len, size, p.history[p.cursor])],
      cursor: p.cursor + 1,
    }));
  }, [flash]);

  return useMemo(
    () => ({
      start: state.history[state.cursor] ?? 0,
      flashing,
      index: state.cursor,
      count: state.history.length,
      canBack: state.cursor > 0,
      canForward: state.cursor < state.history.length - 1,
      back,
      forward,
      next,
      reset,
    }),
    [state, flashing, back, forward, next, reset],
  );
}
